/**
 * Store Availability Module - Storefront API version
 * Refactored for better performance and maintainability
 * 
 * @version 5.5.2
 *
 * Changes from 5.5.1:
 * - BUGFIX: Tilaustuote fallback now works correctly when Storefront API can't access custom.badge metafield
 * - Changed ?? (nullish coalescing) to || (logical OR) in _updateOnlinePanel() for isCustomOrderProduct
 * - Previously, API returning false (not null) prevented fallback to Liquid data-is-custom-order attribute
 * - This caused "Ei saatavilla verkkokaupasta" for products that should show as Tilaustuote
 *
 * Changes from 5.5.0:
 * - BUGFIX: Cart now works correctly when products added via Quick Buy from collection pages
 * - Added FALLBACK_ONLINE_LOCATION_ID constant for Konala location
 * - Cart badge logic now uses CONFIG.onlineLocationId instead of window.MurrenMurkina.config
 * - This ensures proper stock display even when store-availability-growly.liquid is not rendered
 * - Exported CONFIG for use by QuickBuyManager in quick-buy-scripts.liquid
 * 
 * Changes from 5.4.0:
 * - BUGFIX: Cart item badges now use only Konala (online location) stock for online mode
 * - Previously used totalQuantity which incorrectly summed ALL store locations
 * - This caused "Varastossa" badge to show even when Konala had 0 stock but other stores had inventory
 * - Now correctly shows "Ei varastossa" when Konala has no stock in online delivery mode
 * - BUGFIX: Delivery time text now hidden when items are unavailable for online delivery
 * - Added hasUnavailableOnlineItem tracking to _updateCartItemBadges()
 * - Updated _updateDeliveryTimeText() to hide "Toimitus 1-3 arkipäivässä" when items can't be shipped
 * - BUGFIX: All stock checks now handle negative values correctly (changed === 0 to <= 0)
 * - _updateOnlinePanel(): Products with negative stock no longer show as "Saatavilla"
 * - _updateStoreItem(): Store items with negative stock show "Ei varastossa"
 * - formatQuantityText(): Returns null for negative values instead of "-1 kpl"
 * - _canAddToCart(): Pickup validation blocks orders when store has negative stock
 * 
 * Changes from 5.3.0:
 * - NEW: "Valitse vaihtoehto nähdäksesi saatavuuden" panel when no variant selected
 * - Changed from hiding component to showing informative prompt
 * - Added noVariantPanel element handling
 * - Updated _showVariantContent() and _showNoVariantContent() methods
 * 
 * Changes from 5.2.0:
 * - BUGFIX: Hide component when no variant is selected (shows "Ladataan..." forever fix)
 * - Added _hideComponent() and _showComponent() methods
 * - Updated _init() to check for valid variant before showing
 * - Updated variantChange handler to show/hide based on variant selection
 * 
 * Changes from 5.1.0:
 * - BUGFIX: Tilaustuote status now fetched from Storefront API instead of static data attribute
 * - Added product.metafield query for custom.badge to detect tilaustuote dynamically
 * - isCustomOrder now part of availability data, updates correctly on variant change
 * - Cart item badges now correctly show tilaustuote status
 * - Removed dependency on data-is-custom-order attribute (kept as fallback only)
 * 
 * Features:
 * - Tilaustuote detection: availableForSale=true but onlineQty=0 AND custom.badge.value == 'Tilaustuote'
 * - Cart attributes for checkout delivery method pre-selection
 * - Delivery mode tabs sync between product page and cart
 * - Add-to-cart validation for pickup mode
 * - Add-to-cart blocking for online mode when not available
 * - Gift cards excluded from availability checks
 */

(function() {
  'use strict';

  window.MurrenMurkina = window.MurrenMurkina || {};

  // =============================================================================
  // CONFIGURATION - Loaded from Liquid, with fallbacks
  // =============================================================================
  
  // v5.5.1: Added hardcoded fallback for onlineLocationId
  // This ensures cart works even when Quick Buy is used from collection pages
  // where store-availability-growly.liquid is not rendered
  const FALLBACK_ONLINE_LOCATION_ID = 'gid://shopify/Location/94849696022'; // Konala
  
  const CONFIG = {
    // These should be set via Liquid in store-availability-growly.liquid
    storefrontToken: window.MurrenMurkina.config?.storefrontToken || '',
    onlineLocationId: window.MurrenMurkina.config?.onlineLocationId || FALLBACK_ONLINE_LOCATION_ID,
    shopDomain: window.Shopify?.shop || window.location.hostname,
    apiVersion: '2025-01',
    cacheMaxAge: 60000, // 60 seconds cache
    storageKey: 'murrenmurkina_selected_store',
    deliveryModeKey: 'murrenmurkina_delivery_mode',
    cartAttributes: {
      deliveryMode: '_Toimitustapa',
      pickupLocation: '_Noutomyymala',
      pickupLocationLegacy: '_Noutomyymälä',
      pickupLocationCity: '_NoutomyymalaCity',
      pickupLocationAddress: '_NoutomyymalaAddress',
      pickupLocationId: '_NoutomyymalaId'
    }
  };

  // Validate config
  if (!CONFIG.storefrontToken) {
    console.warn('[StoreAvailability] Storefront token not configured. Set window.MurrenMurkina.config.storefrontToken');
  }

  // =============================================================================
  // STOREFRONT API SERVICE - With Batch Queries
  // =============================================================================
  
  const StorefrontAPI = {
    _cache: new Map(),

    /**
     * Get availability for a single variant
     */
    async getVariantAvailability(variantGid) {
      // Check cache first
      const cached = this._cache.get(variantGid);
      if (cached && Date.now() - cached.timestamp < CONFIG.cacheMaxAge) {
        return cached.data;
      }

      // Single variant query
      const result = await this.getMultipleVariantsAvailability([variantGid]);
      return result[variantGid] || null;
    },

    /**
     * Get availability for multiple variants in a single API call
     * This is much more efficient than individual calls
     * 
     * v5.2.0: Now includes product.metafield for tilaustuote detection
     */
    async getMultipleVariantsAvailability(variantGids) {
      if (!variantGids.length) return {};
      if (!CONFIG.storefrontToken) {
        console.error('[StorefrontAPI] No token configured');
        return {};
      }

      // Check which variants we need to fetch (not in cache)
      const uncachedGids = [];
      const result = {};

      for (const gid of variantGids) {
        const cached = this._cache.get(gid);
        if (cached && Date.now() - cached.timestamp < CONFIG.cacheMaxAge) {
          result[gid] = cached.data;
        } else {
          uncachedGids.push(gid);
        }
      }

      // If all were cached, return early
      if (uncachedGids.length === 0) {
        return result;
      }

      // Batch query for uncached variants
      // v5.5.2: Added variant-level tilaustuote metafield query
      const query = `
        query getVariantsAvailability($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on ProductVariant {
              id
              availableForSale
              quantityAvailable
              tilaustuote: metafield(namespace: "custom", key: "tilaustuote") {
                value
              }
              product {
                metafield(namespace: "custom", key: "badge") {
                  value
                }
              }
              storeAvailability(first: 20) {
                edges {
                  node {
                    available
                    quantityAvailable
                    location {
                      id
                      name
                      address {
                        address1
                        city
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      try {
        const response = await fetch(
          `https://${CONFIG.shopDomain}/api/${CONFIG.apiVersion}/graphql.json`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': CONFIG.storefrontToken
            },
            body: JSON.stringify({
              query,
              variables: { ids: uncachedGids }
            })
          }
        );

        if (!response.ok) {
          throw new Error(`Storefront API error: ${response.status}`);
        }

        const json = await response.json();
        
        if (json.errors) {
          console.error('[StorefrontAPI] GraphQL errors:', json.errors);
          throw new Error('GraphQL query failed');
        }

        // Process results
        const nodes = json.data?.nodes || [];
        const timestamp = Date.now();

        nodes.forEach(variant => {
          if (!variant) return;

          // v5.5.2: Check variant-level tilaustuote first, then product-level badge
          const variantTilaustuote = variant.tilaustuote?.value;
          const badgeValue = variant.product?.metafield?.value || '';
          const isCustomOrder = variantTilaustuote === 'true' || badgeValue === 'Tilaustuote';

          const availability = {
            variantId: variant.id,
            availableForSale: variant.availableForSale,
            totalQuantity: variant.quantityAvailable || 0,
            isCustomOrder: isCustomOrder, // v5.5.2: variant-level + product-level
            locations: {}
          };

          variant.storeAvailability?.edges?.forEach(edge => {
            const node = edge.node;
            availability.locations[node.location.id] = {
              name: node.location.name,
              available: node.available,
              quantity: node.quantityAvailable || 0,
              city: node.location.address?.city
            };
          });

          // Cache and add to result
          this._cache.set(variant.id, { data: availability, timestamp });
          result[variant.id] = availability;
        });

        return result;

      } catch (error) {
        console.error('[StorefrontAPI] Failed to fetch variants:', error);
        return result; // Return what we have from cache
      }
    },

    clearCache() {
      this._cache.clear();
    }
  };

  // =============================================================================
  // UTILITY FUNCTIONS
  // =============================================================================
  
  const Utils = {
    storage: {
      _memoryFallback: {},
      
      get(key) {
        try {
          const item = localStorage.getItem(key);
          return item ? JSON.parse(item) : null;
        } catch (e) {
          return this._memoryFallback[key] || null;
        }
      },
      
      set(key, value) {
        try {
          localStorage.setItem(key, JSON.stringify(value));
        } catch (e) {
          this._memoryFallback[key] = value;
        }
      },
      
      remove(key) {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          delete this._memoryFallback[key];
        }
      }
    },

    getStoreConfig() {
      const element = document.getElementById('store-config-data');
      if (!element) return null;
      try {
        return JSON.parse(element.textContent);
      } catch (e) {
        console.error('[Utils] Failed to parse store config:', e);
        return null;
      }
    },

    variantIdToGid(variantId) {
      const idString = String(variantId);
      if (idString.startsWith('gid://')) return idString;
      return `gid://shopify/ProductVariant/${idString}`;
    },

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    // v5.5.0: Changed from === 0 to <= 0 to handle negative stock values
    formatQuantityText(quantity) {
      if (quantity <= 0) return null;
      if (quantity < 5) return `${quantity} kpl`;
      if (quantity < 10) return 'Yli 5 saatavilla';
      if (quantity < 20) return 'Yli 10 saatavilla';
      return 'Yli 20 saatavilla';
    },

    /**
     * Shared method to refresh cart items from Shopify API
     * Filters out gift cards automatically
     */
    async refreshCartItems() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        // Filter out gift cards - they don't have physical inventory
        window.MurrenMurkina.cartItems = cart.items
          .filter(item => !item.gift_card)
          .map(item => ({
            variantId: String(item.variant_id),
            variantGid: this.variantIdToGid(item.variant_id),
            productTitle: item.product_title,
            variantTitle: item.variant_title,
            quantity: item.quantity
          }));
        
        return window.MurrenMurkina.cartItems;
      } catch (error) {
        console.error('[Utils] Failed to refresh cart items:', error);
        return [];
      }
    }
  };

  // =============================================================================
  // STORE DATA SERVICE
  // =============================================================================
  
  const StoreDataService = {
    getSelectedStore() {
      return Utils.storage.get(CONFIG.storageKey);
    },

    setSelectedStore(storeData) {
      Utils.storage.set(CONFIG.storageKey, storeData);
      document.dispatchEvent(new CustomEvent('store:selected', { detail: storeData }));
    },

    clearSelectedStore() {
      Utils.storage.remove(CONFIG.storageKey);
      document.dispatchEvent(new CustomEvent('store:cleared'));
    }
  };

  // =============================================================================
  // DELIVERY MODE SERVICE
  // =============================================================================
  
  const DeliveryModeService = {
    getMode() {
      const mode = Utils.storage.get(CONFIG.deliveryModeKey);
      return mode === 'pickup' ? 'pickup' : 'online';
    },

    setMode(mode) {
      const validMode = mode === 'pickup' ? 'pickup' : 'online';
      Utils.storage.set(CONFIG.deliveryModeKey, validMode);
      
      // Update cart attributes when mode changes
      CartAttributesService.updateForDeliveryMode(validMode);
      
      document.dispatchEvent(new CustomEvent('deliveryMode:changed', { detail: { mode: validMode } }));
    },

    isPickupMode() {
      return this.getMode() === 'pickup';
    }
  };

  // =============================================================================
  // CART ATTRIBUTES SERVICE (for checkout pre-selection)
  // =============================================================================
  
  const CartAttributesService = {
    _pendingUpdate: null,

    /**
     * Parse "Mankkaantie 2-4, 02200 Espoo" tai "Mankkaantie 2-4, Espoo"
     * → { address1: "Mankkaantie 2-4", city: "Espoo" }
     * Strippaa 5-numeroinen postinumero kaupunkiosasta.
     */
    _parseStoreAddress(rawAddress) {
      if (!rawAddress || typeof rawAddress !== 'string') {
        return { address1: '', city: '' };
      }
      const parts = rawAddress.split(',').map(p => p.trim()).filter(Boolean);
      if (parts.length === 0) return { address1: '', city: '' };
      const address1 = parts[0] || '';
      const cityRaw = parts.slice(1).join(', ');
      const city = cityRaw.replace(/\b\d{5}\b/g, '').replace(/\s{2,}/g, ' ').trim();
      return { address1, city };
    },

    _buildPickupAttributes(selectedStore) {
      const parsed = this._parseStoreAddress(selectedStore?.address);
      const name = selectedStore?.name || '';
      return {
        [CONFIG.cartAttributes.deliveryMode]: 'pickup',
        [CONFIG.cartAttributes.pickupLocation]: name,
        [CONFIG.cartAttributes.pickupLocationLegacy]: name,
        [CONFIG.cartAttributes.pickupLocationCity]: parsed.city,
        [CONFIG.cartAttributes.pickupLocationAddress]: parsed.address1,
        [CONFIG.cartAttributes.pickupLocationId]: selectedStore?.locationId || ''
      };
    },

    _buildClearedAttributes(deliveryMode) {
      return {
        [CONFIG.cartAttributes.deliveryMode]: deliveryMode,
        [CONFIG.cartAttributes.pickupLocation]: '',
        [CONFIG.cartAttributes.pickupLocationLegacy]: '',
        [CONFIG.cartAttributes.pickupLocationCity]: '',
        [CONFIG.cartAttributes.pickupLocationAddress]: '',
        [CONFIG.cartAttributes.pickupLocationId]: ''
      };
    },

    async updateForDeliveryMode(mode) {
      const selectedStore = StoreDataService.getSelectedStore();

      const attributes = mode === 'pickup'
        ? (selectedStore
            ? this._buildPickupAttributes(selectedStore)
            : this._buildClearedAttributes('pickup'))
        : this._buildClearedAttributes('ship');

      return this._updateCartAttributes(attributes);
    },

    async updateForStoreSelection(storeData) {
      if (!DeliveryModeService.isPickupMode()) return;

      const attributes = this._buildPickupAttributes(storeData);
      return this._updateCartAttributes(attributes);
    },

    async clearPickupAttributes() {
      const attributes = this._buildClearedAttributes('ship');
      return this._updateCartAttributes(attributes);
    },

    async _updateCartAttributes(attributes) {
      // Debounce rapid updates
      if (this._pendingUpdate) {
        clearTimeout(this._pendingUpdate);
      }

      return new Promise((resolve) => {
        this._pendingUpdate = setTimeout(async () => {
          try {
            const response = await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ attributes })
            });

            if (!response.ok) {
              throw new Error(`Failed to update cart attributes: ${response.status}`);
            }

            const cart = await response.json();
            resolve(cart);
          } catch (error) {
            console.error('[CartAttributes] Error updating:', error);
            resolve(null);
          }
        }, 100);
      });
    }
  };

  // =============================================================================
  // STORE AVAILABILITY COMPONENT (Product Page)
  // =============================================================================
  
  class StoreAvailability extends HTMLElement {
    constructor() {
      super();
      this._boundHandlers = {};
      this._currentAvailability = null;
      this._canOrderOnline = true; // Track if online ordering is possible
      this._originalButtonText = null; // Store original button text
      this._hasVariant = false; // v5.4.0: Track if variant is selected
    }

    connectedCallback() {
      this.modal = document.querySelector('[data-store-modal="product"]');
      this.triggers = this.querySelectorAll('[data-store-trigger]');
      this.selectedStoreRow = this.querySelector('[data-selected-store-row]');
      this.selectStoreRow = this.querySelector('[data-select-store-row]');
      this.pickupUnavailable = this.querySelector('[data-pickup-unavailable]');
      this.pickupNote = this.querySelector('[data-pickup-note]');
      this.config = Utils.getStoreConfig();
      
      // Tab elements
      this.tabs = this.querySelectorAll('[data-delivery-tab]');
      this.panels = this.querySelectorAll('[data-delivery-panel]');
      this.pickupValidation = this.querySelector('[data-pickup-validation]');
      
      // v5.4.0: No variant panel and tabs container
      this.noVariantPanel = this.querySelector('[data-no-variant-panel]');
      this.deliveryTabs = this.querySelector('[data-delivery-tabs]');
      
      // Online panel info sections
      this.onlineInfoNormal = this.querySelector('[data-online-info="normal"]');
      this.onlineInfoCustomOrder = this.querySelector('[data-online-info="custom-order"]');
      this.onlineInfoUnavailable = this.querySelector('[data-online-info="unavailable"]');
      this.onlineStatus = this.querySelector('[data-online-status]');
      
      this._init();
    }

    disconnectedCallback() {
      this._removeEventListeners();
    }

    /**
     * v5.4.0: Show "no variant" prompt, hide tabs and panels
     */
    _showNoVariantContent() {
      this._hasVariant = false;
      
      // Show no-variant panel
      if (this.noVariantPanel) {
        this.noVariantPanel.style.display = '';
      }
      
      // Hide tabs
      if (this.deliveryTabs) {
        this.deliveryTabs.style.display = 'none';
      }
      
      // Hide all delivery panels
      this.panels.forEach(panel => {
        panel.style.display = 'none';
      });
    }

    /**
     * v5.4.0: Show tabs and panels, hide "no variant" prompt
     */
    _showVariantContent() {
      this._hasVariant = true;
      
      // Hide no-variant panel
      if (this.noVariantPanel) {
        this.noVariantPanel.style.display = 'none';
      }
      
      // Show tabs
      if (this.deliveryTabs) {
        this.deliveryTabs.style.display = '';
      }
      
      // Show correct panel based on current delivery mode
      this._loadDeliveryMode();
    }

    /**
     * v5.4.0: Check if variant ID is valid
     */
    _isValidVariantId(variantId) {
      return variantId && variantId !== '' && variantId !== 'null' && variantId !== 'undefined';
    }

    async _init() {
      this._setupEventListeners();
      this._setupTabListeners();
      this._setupAddToCartInterception();
      
      const variantGid = this.dataset.variantGid;
      const variantId = this.dataset.variantId;
      
      // v5.4.0: Show appropriate content based on variant selection
      if (!this._isValidVariantId(variantId)) {
        this._showNoVariantContent();
        return;
      }
      
      this._showVariantContent();
      
      if (variantGid) {
        await this._fetchAndUpdateAvailability(variantGid);
      }
      
      this._loadSelectedStore();
    }

    _setupTabListeners() {
      this.tabs.forEach(tab => {
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          const mode = tab.dataset.deliveryTab;
          this._switchTab(mode);
          DeliveryModeService.setMode(mode);
        });
      });
    }

    _switchTab(mode) {
      this.tabs.forEach(tab => {
        const isActive = tab.dataset.deliveryTab === mode;
        tab.classList.toggle('delivery-tab--active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.panels.forEach(panel => {
        const isActive = panel.dataset.deliveryPanel === mode;
        panel.style.display = isActive ? '' : 'none';
        panel.classList.toggle('delivery-panel--active', isActive);
      });

      if (this.pickupValidation) {
        this.pickupValidation.style.display = 'none';
      }

      // Update Add to Cart button state when tab changes
      this._updateAddToCartButton();
    }

    _loadDeliveryMode() {
      const mode = DeliveryModeService.getMode();
      this._switchTab(mode);
    }

    _setupAddToCartInterception() {
      const productForm = document.querySelector('form[action*="/cart/add"]');
      if (!productForm) return;

      const validateHandler = (e) => {
        if (!this._validateAddToCart()) {
          e.preventDefault();
          e.stopPropagation();
          this._showPickupValidation();
        }
      };

      productForm.addEventListener('submit', validateHandler);
      this._boundHandlers.formSubmit = { element: productForm, handler: validateHandler };

      const addToCartBtn = productForm.querySelector('[type="submit"], .add-to-cart, [data-add-to-cart]');
      if (addToCartBtn) {
        // Store original button text
        this._originalButtonText = addToCartBtn.textContent;
        
        addToCartBtn.addEventListener('click', validateHandler, true);
        this._boundHandlers.addToCartClick = { element: addToCartBtn, handler: validateHandler };
      }
    }

    _validateAddToCart() {
      // v5.4.0: If no variant selected, don't validate (let Shopify handle it)
      if (!this._hasVariant) {
        return true;
      }
      
      // If online mode and cannot order online, block
      if (!DeliveryModeService.isPickupMode() && !this._canOrderOnline) {
        return false;
      }
      
      // If pickup mode, validate store selection and availability
      if (DeliveryModeService.isPickupMode()) {
        const selectedStore = StoreDataService.getSelectedStore();
        if (!selectedStore) return false;
        
        if (this._currentAvailability) {
          const storeData = this._currentAvailability.locations[selectedStore.locationId];
          // v5.5.0: Changed from === 0 to <= 0 to handle negative stock values
          if (!storeData || storeData.quantity <= 0) {
            return false;
          }
        }
      }
      
      return true;
    }

    _showPickupValidation() {
      if (this.pickupValidation) {
        this.pickupValidation.style.display = 'flex';
        this.pickupValidation.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        setTimeout(() => {
          if (this.pickupValidation) {
            this.pickupValidation.style.display = 'none';
          }
        }, 5000);
      }
    }

    /**
     * Update Add to Cart button based on delivery mode and availability
     */
    _updateAddToCartButton() {
      const addToCartBtn = document.querySelector('form[action*="/cart/add"] [type="submit"], form[action*="/cart/add"] .add-to-cart, form[action*="/cart/add"] [data-add-to-cart]');
      if (!addToCartBtn) return;

      // Store original text if not already stored
      if (!this._originalButtonText) {
        this._originalButtonText = addToCartBtn.textContent;
      }

      // v5.4.0: Don't modify button if no variant selected
      if (!this._hasVariant) {
        return;
      }

      const isOnlineMode = !DeliveryModeService.isPickupMode();
      const variantAvailable = this.dataset.variantAvailable === 'true';

      // If online mode and cannot order online
      if (isOnlineMode && !this._canOrderOnline) {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Ei saatavilla verkkokaupasta';
        addToCartBtn.classList.add('btn--disabled-online');
      } else {
        // Restore normal state (respect variant.available from Shopify)
        addToCartBtn.disabled = !variantAvailable;
        addToCartBtn.textContent = variantAvailable ? this._originalButtonText : 'Ei saatavilla';
        addToCartBtn.classList.remove('btn--disabled-online');
      }
    }

    _setupEventListeners() {
      // Modal triggers
      this.triggers.forEach(trigger => {
        const handler = (e) => {
          e.preventDefault();
          this._openModal();
        };
        trigger.addEventListener('click', handler);
        this._boundHandlers[`trigger_${Array.from(this.triggers).indexOf(trigger)}`] = { element: trigger, handler };
      });

      // Modal close and select
      if (this.modal) {
        const closeBtn = this.modal.querySelector('[data-store-modal-close]');
        if (closeBtn) {
          const closeHandler = () => this._closeModal();
          closeBtn.addEventListener('click', closeHandler);
          this._boundHandlers.modalClose = { element: closeBtn, handler: closeHandler };
        }

        const backdropHandler = (e) => {
          if (e.target === this.modal) this._closeModal();
        };
        this.modal.addEventListener('click', backdropHandler);
        this._boundHandlers.modalBackdrop = { element: this.modal, handler: backdropHandler };

        this.modal.querySelectorAll('[data-store-select-btn]').forEach((btn, i) => {
          const selectHandler = (e) => {
            e.stopPropagation();
            const item = btn.closest('[data-store-item]');
            if (item) this._selectStore(item);
          };
          btn.addEventListener('click', selectHandler);
          this._boundHandlers[`storeSelect_${i}`] = { element: btn, handler: selectHandler };
        });
      }

      // Keyboard
      this._boundHandlers.keydown = (e) => {
        if (e.key === 'Escape' && this.modal?.classList.contains('is-active')) {
          this._closeModal();
        }
      };
      document.addEventListener('keydown', this._boundHandlers.keydown);

      // Variant change - v5.4.0: Show variant content when variant selected
      this._boundHandlers.variantChange = async (e) => {
        const variant = e.detail?.variant;
        
        // v5.4.0: Show no-variant content if no variant selected
        if (!variant || !variant.id) {
          this._showNoVariantContent();
          return;
        }
        
        this._showVariantContent();
        
        const variantGid = Utils.variantIdToGid(variant.id);
        this.dataset.variantGid = variantGid;
        this.dataset.variantId = variant.id;
        this.dataset.variantAvailable = variant.available;
        
        StorefrontAPI.clearCache();
        await this._fetchAndUpdateAvailability(variantGid);
      };
      document.addEventListener('on:variant:change', this._boundHandlers.variantChange);
      document.addEventListener('variant:change', this._boundHandlers.variantChange);

      // Store selected
      this._boundHandlers.storeSelected = () => {
        if (this.pickupValidation) {
          this.pickupValidation.style.display = 'none';
        }
        this._updateSelectedStoreDisplay();
      };
      document.addEventListener('store:selected', this._boundHandlers.storeSelected);

      // Delivery mode changed (from other components like cart)
      this._boundHandlers.deliveryModeChanged = () => {
        this._updateAddToCartButton();
      };
      document.addEventListener('deliveryMode:changed', this._boundHandlers.deliveryModeChanged);
    }

    _removeEventListeners() {
      // Remove document-level listeners
      if (this._boundHandlers.keydown) {
        document.removeEventListener('keydown', this._boundHandlers.keydown);
      }
      if (this._boundHandlers.variantChange) {
        document.removeEventListener('on:variant:change', this._boundHandlers.variantChange);
        document.removeEventListener('variant:change', this._boundHandlers.variantChange);
      }
      if (this._boundHandlers.storeSelected) {
        document.removeEventListener('store:selected', this._boundHandlers.storeSelected);
      }
      if (this._boundHandlers.deliveryModeChanged) {
        document.removeEventListener('deliveryMode:changed', this._boundHandlers.deliveryModeChanged);
      }
      
      // Remove element-specific listeners
      Object.keys(this._boundHandlers).forEach(key => {
        const binding = this._boundHandlers[key];
        if (binding?.element && binding?.handler) {
          binding.element.removeEventListener('click', binding.handler);
          binding.element.removeEventListener('submit', binding.handler);
        }
      });
      
      this._boundHandlers = {};
    }

    async _fetchAndUpdateAvailability(variantGid) {
      const data = await StorefrontAPI.getVariantAvailability(variantGid);
      if (data) {
        this._currentAvailability = data;
        this._updateDisplay(data);
      }
    }

    _updateDisplay(availability) {
      if (!this.config) return;

      const onlineData = availability.locations[this.config.onlineLocationId];
      const onlineQty = onlineData?.quantity || 0;
      
      let totalQtyAllLocations = 0;
      Object.values(availability.locations).forEach(locationData => {
        totalQtyAllLocations += locationData.quantity || 0;
      });
      
      // v5.2.0: Pass isCustomOrder from availability data (from API)
      this._updateOnlinePanel(availability.availableForSale, onlineQty, totalQtyAllLocations, availability.isCustomOrder);

      const countEl = this.querySelector('[data-stores-count]');
      let availableStoreCount = 0;
      
      if (countEl) {
        this.config.stores.forEach(store => {
          const storeData = availability.locations[store.locationId];
          if (storeData?.quantity > 0) availableStoreCount++;
        });

        if (availableStoreCount > 0) {
          countEl.innerHTML = `<span class="store-availability__count--available">Saatavilla ${availableStoreCount} myymälässä</span>`;
        } else {
          countEl.innerHTML = '<span>Ei saatavilla myymälöissä</span>';
        }
      }

      this._updatePickupPanelState(availableStoreCount);

      this.modal?.querySelectorAll('[data-store-item]').forEach(item => {
        const locationId = item.dataset.locationId;
        const storeData = availability.locations[locationId];
        const quantity = storeData?.quantity || 0;
        this._updateStoreItem(item, quantity);
      });

      this._updateSelectedStoreDisplay();
    }

    /**
     * v5.5.0: Fixed to handle negative stock values (onlineQty <= 0 instead of === 0)
     * v5.2.0: isCustomOrder now passed as parameter from API data
     * No longer reads from data-is-custom-order attribute
     */
    _updateOnlinePanel(availableForSale, onlineQty, totalQtyAllLocations, isCustomOrder) {
      if (this.onlineInfoNormal) this.onlineInfoNormal.style.display = 'none';
      if (this.onlineInfoCustomOrder) this.onlineInfoCustomOrder.style.display = 'none';
      if (this.onlineInfoUnavailable) this.onlineInfoUnavailable.style.display = 'none';

      // v5.5.2: Changed ?? to || so fallback triggers when API returns false (not just null/undefined)
      // Storefront API may not have access to custom.badge metafield, returning false instead of null
      // Liquid-rendered data attribute is more reliable as it has full metafield access
      const isCustomOrderProduct = isCustomOrder || (this.dataset.isCustomOrder === 'true');

      // Reset - track if online ordering is possible
      this._canOrderOnline = false;

      if (!availableForSale) {
        // Product not available for sale at all
        if (this.onlineInfoUnavailable) {
          this.onlineInfoUnavailable.style.display = '';
        }
        // _canOrderOnline stays false
      } else if (onlineQty <= 0) {
        // v5.5.0: Changed from === 0 to <= 0 to handle negative stock values
        // availableForSale=true but Konala has no stock (or negative)
        if (isCustomOrderProduct) {
          // Tilaustuote - can order from supplier
          if (this.onlineInfoCustomOrder) {
            this.onlineInfoCustomOrder.style.display = '';
          }
          this._canOrderOnline = true; // Can order - it's a custom order product
        } else {
          // Not a custom order product, no online stock → cannot order online
          if (this.onlineInfoUnavailable) {
            this.onlineInfoUnavailable.style.display = '';
          }
          // _canOrderOnline stays false - cannot order online
        }
      } else {
        // Konala has stock → available
        if (this.onlineInfoNormal) {
          this.onlineInfoNormal.style.display = '';
        }
        
        if (this.onlineStatus) {
          this.onlineStatus.innerHTML = `
            <span class="store-availability__status--available">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor"/>
              </svg>
              Saatavilla
            </span>
          `;
        }
        this._canOrderOnline = true; // Konala has stock - can order
      }

      // Update button state immediately
      this._updateAddToCartButton();
    }

    _updatePickupPanelState(availableStoreCount) {
      const hasStoresWithStock = availableStoreCount > 0;

      if (this.pickupUnavailable) {
        this.pickupUnavailable.style.display = hasStoresWithStock ? 'none' : '';
      }

      if (!hasStoresWithStock) {
        if (this.selectStoreRow) this.selectStoreRow.style.display = 'none';
        if (this.selectedStoreRow) this.selectedStoreRow.style.display = 'none';
        if (this.pickupNote) this.pickupNote.style.display = 'none';
      } else {
        if (this.pickupNote) this.pickupNote.style.display = '';
      }
    }

    _updateStoreItem(item, quantity) {
      const statusEl = item.querySelector('[data-store-status]');
      if (!statusEl) return;

      const qtyText = Utils.formatQuantityText(quantity);

      // v5.5.0: Changed from === 0 to <= 0 to handle negative stock values
      if (quantity <= 0) {
        statusEl.innerHTML = `
          <span class="store-indicator store-indicator--out">
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <circle cx="6" cy="6" r="6" fill="currentColor"/>
            </svg>
          </span>
          <span class="store-status-text">Ei varastossa</span>
        `;
        statusEl.dataset.quantity = 0;
      } else if (quantity <= 2) {
        statusEl.innerHTML = `
          <span class="store-indicator store-indicator--low">
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <circle cx="6" cy="6" r="6" fill="currentColor"/>
            </svg>
          </span>
          <span class="store-status-text">Vähän varastossa (${qtyText})</span>
        `;
        statusEl.dataset.quantity = quantity;
      } else {
        statusEl.innerHTML = `
          <span class="store-indicator store-indicator--in-stock">
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <circle cx="6" cy="6" r="6" fill="currentColor"/>
            </svg>
          </span>
          <span class="store-status-text">Saatavilla (${qtyText})</span>
        `;
        statusEl.dataset.quantity = quantity;
      }
    }

    _openModal() {
      if (!this.modal) return;
      this._sortStores();
      this._highlightSelectedStore();
      this.modal.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    }

    _closeModal() {
      if (!this.modal) return;
      this.modal.classList.remove('is-active');
      document.body.style.overflow = '';
    }

    _selectStore(storeItem) {
      const storeData = {
        name: storeItem.dataset.storeName,
        locationId: storeItem.dataset.locationId,
        address: storeItem.dataset.address
      };
      
      StoreDataService.setSelectedStore(storeData);
      CartAttributesService.updateForStoreSelection(storeData);
      this._updateSelectedStoreDisplay();
      this._closeModal();
    }

    _loadSelectedStore() {
      const savedStore = StoreDataService.getSelectedStore();
      if (savedStore) {
        this._updateSelectedStoreDisplay();
      }
    }

    _highlightSelectedStore() {
      const savedStore = StoreDataService.getSelectedStore();
      const items = this.modal?.querySelectorAll('[data-store-item]') || [];

      items.forEach(item => {
        const isSelected = savedStore && item.dataset.locationId === savedStore.locationId;
        item.classList.toggle('store-item--selected', isSelected);
        
        const btn = item.querySelector('[data-store-select-btn]');
        if (btn) {
          btn.textContent = isSelected ? 'Valittu' : 'Valitse';
          btn.classList.toggle('store-select-btn--selected', isSelected);
        }
      });
    }

    _updateSelectedStoreDisplay() {
      const savedStore = StoreDataService.getSelectedStore();
      
      let hasStoresWithStock = false;
      if (this._currentAvailability && this.config) {
        this.config.stores.forEach(store => {
          const storeData = this._currentAvailability.locations[store.locationId];
          if (storeData?.quantity > 0) hasStoresWithStock = true;
        });
      }

      if (!hasStoresWithStock) {
        if (this.selectedStoreRow) this.selectedStoreRow.style.display = 'none';
        if (this.selectStoreRow) this.selectStoreRow.style.display = 'none';
        return;
      }

      if (savedStore && this.selectedStoreRow && this.selectStoreRow) {
        let selectedStoreHasStock = false;
        if (this._currentAvailability) {
          const storeData = this._currentAvailability.locations[savedStore.locationId];
          selectedStoreHasStock = storeData?.quantity > 0;
        }

        if (selectedStoreHasStock) {
          this.selectedStoreRow.style.display = '';
          this.selectStoreRow.style.display = 'none';
          
          const nameEl = this.selectedStoreRow.querySelector('[data-selected-store-name]');
          if (nameEl) nameEl.textContent = savedStore.name;
          
          const statusEl = this.selectedStoreRow.querySelector('[data-selected-store-status]');
          if (statusEl && this._currentAvailability) {
            const storeData = this._currentAvailability.locations[savedStore.locationId];
            const quantity = storeData?.quantity || 0;
            const qtyText = Utils.formatQuantityText(quantity);
            
            statusEl.innerHTML = `
              <span class="store-availability__status--available">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor"/>
                </svg>
                Saatavilla (${qtyText})
              </span>
            `;
          }
        } else {
          this.selectedStoreRow.style.display = 'none';
          this.selectStoreRow.style.display = '';
        }
      } else if (this.selectedStoreRow && this.selectStoreRow) {
        this.selectedStoreRow.style.display = 'none';
        this.selectStoreRow.style.display = '';
      }
    }

    _sortStores() {
      const grid = this.modal?.querySelector('[data-store-grid]');
      if (!grid) return;

      const items = Array.from(grid.querySelectorAll('[data-store-item]'));
      
      items.sort((a, b) => {
        const qtyA = parseInt(a.querySelector('[data-store-status]')?.dataset.quantity) || 0;
        const qtyB = parseInt(b.querySelector('[data-store-status]')?.dataset.quantity) || 0;
        
        if (qtyA > 0 && qtyB === 0) return -1;
        if (qtyA === 0 && qtyB > 0) return 1;
        return qtyB - qtyA;
      });

      items.forEach(item => grid.appendChild(item));
    }
  }

  // =============================================================================
  // STANDALONE STORE MODAL (Cart)
  // =============================================================================
  
  class StandaloneStoreModal {
    constructor() {
      this.modal = document.querySelector('[data-store-modal="standalone"]');
      if (!this.modal) return;

      if (this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }

      this.storeItems = this.modal.querySelectorAll('[data-store-item]');
      this._cartAvailability = {};
      this._boundHandlers = {};
      this._init();
    }

    _init() {
      const closeBtn = this.modal.querySelector('[data-store-modal-close]');
      if (closeBtn) {
        this._boundHandlers.close = () => this.close();
        closeBtn.addEventListener('click', this._boundHandlers.close);
      }

      this._boundHandlers.backdrop = (e) => {
        if (e.target === this.modal) this.close();
      };
      this.modal.addEventListener('click', this._boundHandlers.backdrop);

      this._boundHandlers.keydown = (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('is-active')) {
          this.close();
        }
      };
      document.addEventListener('keydown', this._boundHandlers.keydown);

      this.modal.querySelectorAll('[data-store-select-btn]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const item = btn.closest('[data-store-item]');
          if (item) this._selectStore(item);
        });
      });

      this._boundHandlers.open = () => this.open();
      document.addEventListener('standalone-store-modal:open', this._boundHandlers.open);
    }

    destroy() {
      if (this._boundHandlers.keydown) {
        document.removeEventListener('keydown', this._boundHandlers.keydown);
      }
      if (this._boundHandlers.open) {
        document.removeEventListener('standalone-store-modal:open', this._boundHandlers.open);
      }
    }

    async open() {
      this._highlightSelectedStore();
      this.modal.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      
      await Utils.refreshCartItems();
      await this._fetchCartAvailability();
      this._updateAvailabilityBadges();
    }

    close() {
      this.modal.classList.remove('is-active');
      document.body.style.overflow = '';
    }

    async _fetchCartAvailability() {
      const cartItems = window.MurrenMurkina.cartItems || [];
      if (cartItems.length === 0) return;

      const variantGids = cartItems.map(item => item.variantGid);
      this._cartAvailability = await StorefrontAPI.getMultipleVariantsAvailability(variantGids);
    }

    _updateAvailabilityBadges() {
      const cartItems = window.MurrenMurkina.cartItems || [];
      const subtitle = this.modal.querySelector('[data-modal-subtitle]');

      if (cartItems.length === 0) {
        this.storeItems.forEach(item => {
          const badge = item.querySelector('.store-availability-badge');
          if (badge) badge.remove();
        });
        if (subtitle) subtitle.style.display = 'none';
        return;
      }

      if (subtitle) subtitle.style.display = 'block';

      const storesData = [];

      this.storeItems.forEach(item => {
        const locationId = item.dataset.locationId;
        let availableCount = 0;
        let totalNeeded = cartItems.length;

        cartItems.forEach(cartItem => {
          const availData = this._cartAvailability[cartItem.variantGid];
          if (availData) {
            const storeQty = availData.locations[locationId]?.quantity || 0;
            if (storeQty >= cartItem.quantity) availableCount++;
          }
        });

        storesData.push({ element: item, availableCount, totalNeeded, locationId });

        const existingBadge = item.querySelector('.store-availability-badge');
        if (existingBadge) existingBadge.remove();

        const badge = this._createAvailabilityBadge(availableCount, totalNeeded);
        const bottomRow = item.querySelector('[data-bottom-row]');
        const button = bottomRow?.querySelector('[data-store-select-btn]');
        if (badge && bottomRow && button) {
          bottomRow.insertBefore(badge, button);
        }
      });

      this._sortByAvailability(storesData);
    }

    _createAvailabilityBadge(available, total) {
      if (total === 0) return null;

      const badge = document.createElement('div');
      badge.className = 'store-availability-badge';

      if (available === total) {
        badge.classList.add('store-availability-badge--all');
        badge.innerHTML = `
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Kaikki tuotteet saatavilla
        `;
      } else if (available > 0) {
        badge.classList.add('store-availability-badge--partial');
        badge.innerHTML = `
          <div class="store-availability-badge__text">
            <div style="display: flex; align-items: center; gap: 6px;">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <circle cx="6" cy="6" r="5" stroke="currentColor" stroke-width="2" fill="none"/>
                <path d="M6 3V7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              ${available}/${total} tuotetta saatavilla
            </div>
            <span class="store-availability-badge__warning">Nouto ei mahdollinen</span>
          </div>
        `;
      } else {
        badge.classList.add('store-availability-badge--none');
        badge.innerHTML = `
          <div class="store-availability-badge__text">
            <div style="display: flex; align-items: center; gap: 6px;">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              Ei tuotteita saatavilla
            </div>
            <span class="store-availability-badge__warning">Nouto ei mahdollinen</span>
          </div>
        `;
      }

      return badge;
    }

    _sortByAvailability(storesData) {
      const grid = this.modal.querySelector('[data-store-grid]');
      if (!grid) return;

      storesData.sort((a, b) => {
        const aAll = a.availableCount === a.totalNeeded && a.totalNeeded > 0;
        const bAll = b.availableCount === b.totalNeeded && b.totalNeeded > 0;

        if (aAll && !bAll) return -1;
        if (!aAll && bAll) return 1;
        return b.availableCount - a.availableCount;
      });

      storesData.forEach(store => grid.appendChild(store.element));
    }

    _selectStore(storeItem) {
      const storeData = {
        name: storeItem.dataset.storeName,
        locationId: storeItem.dataset.locationId,
        address: storeItem.dataset.address
      };

      DeliveryModeService.setMode('pickup');
      StoreDataService.setSelectedStore(storeData);
      CartAttributesService.updateForStoreSelection(storeData);
      this.close();
    }

    _highlightSelectedStore() {
      const savedStore = StoreDataService.getSelectedStore();

      this.storeItems.forEach(item => {
        const isSelected = savedStore && item.dataset.locationId === savedStore.locationId;
        item.classList.toggle('store-item--selected', isSelected);

        const btn = item.querySelector('[data-store-select-btn]');
        if (btn) {
          btn.textContent = isSelected ? 'Valittu' : 'Valitse';
          btn.classList.toggle('store-select-btn--selected', isSelected);
        }
      });
    }
  }

  // =============================================================================
  // CART STORE WARNING
  // =============================================================================
  
  class CartStoreWarning {
    constructor() {
      this.warningEl = document.querySelector('[data-cart-store-warning]');
      this.selectorEl = document.querySelector('[data-cart-store-selector]');
      this.pickupContent = document.querySelector('[data-cart-pickup-content]');
      this.onlineInfo = document.querySelector('[data-cart-delivery-info="online"]');
      this.cartTabs = document.querySelectorAll('[data-cart-delivery-tab]');
      
      if (!this.cartTabs.length) return;

      this._cartAvailability = {};
      this._boundHandlers = {};
      this._init();
    }

    _init() {
      this._setupCartTabListeners();
      this._loadDeliveryMode();
      this._updateSelectorDisplay();

      const selectorBtn = document.querySelector('[data-cart-store-selector-btn]');
      if (selectorBtn) {
        selectorBtn.addEventListener('click', () => this._openStoreModal());
      }

      const changeBtn = document.querySelector('[data-cart-store-change-btn]');
      if (changeBtn) {
        changeBtn.addEventListener('click', () => this._openStoreModal());
      }

      this._checkAvailability();

      // Debounced cart change handler
      this._boundHandlers.cartChange = Utils.debounce(async () => {
        StorefrontAPI.clearCache();
        await Utils.refreshCartItems();
        await this._checkAvailability();
      }, 200);

      // Symmetry theme events (primary) - NO MutationObserver needed!
      document.addEventListener('on:cart:change', this._boundHandlers.cartChange);
      document.addEventListener('on:cart:after-merge', this._boundHandlers.cartChange);

      this._boundHandlers.storeSelected = () => {
        this._updateSelectorDisplay();
        setTimeout(() => this._checkAvailability(), 100);
      };
      document.addEventListener('store:selected', this._boundHandlers.storeSelected);

      this._boundHandlers.deliveryModeChanged = (e) => {
        this._syncCartTabs(e.detail?.mode);
        this._updateVisibilityByDeliveryMode();
        if (e.detail?.mode === 'pickup') {
          this._checkAvailability();
        }
      };
      document.addEventListener('deliveryMode:changed', this._boundHandlers.deliveryModeChanged);

      // Cart drawer open - Symmetry theme event
      this._boundHandlers.cartDrawerOpen = () => {
        this._loadDeliveryMode();
        this._updateSelectorDisplay();
        Utils.refreshCartItems().then(() => this._checkAvailability());
      };
      document.addEventListener('on:cart-drawer:after-open', this._boundHandlers.cartDrawerOpen);
    }

    destroy() {
      document.removeEventListener('on:cart:change', this._boundHandlers.cartChange);
      document.removeEventListener('on:cart:after-merge', this._boundHandlers.cartChange);
      document.removeEventListener('store:selected', this._boundHandlers.storeSelected);
      document.removeEventListener('deliveryMode:changed', this._boundHandlers.deliveryModeChanged);
      document.removeEventListener('on:cart-drawer:after-open', this._boundHandlers.cartDrawerOpen);
    }

    _setupCartTabListeners() {
      this.cartTabs.forEach(tab => {
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          const mode = tab.dataset.cartDeliveryTab;
          this._switchCartTab(mode);
          DeliveryModeService.setMode(mode);
        });
      });
    }

    _switchCartTab(mode) {
      this.cartTabs.forEach(tab => {
        const isActive = tab.dataset.cartDeliveryTab === mode;
        tab.classList.toggle('cart-delivery-tab--active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this._updateVisibilityByDeliveryMode();
    }

    _syncCartTabs(mode) {
      this.cartTabs.forEach(tab => {
        const isActive = tab.dataset.cartDeliveryTab === mode;
        tab.classList.toggle('cart-delivery-tab--active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    _loadDeliveryMode() {
      const mode = DeliveryModeService.getMode();
      this._syncCartTabs(mode);
      this._updateVisibilityByDeliveryMode();
    }

    _updateVisibilityByDeliveryMode() {
      const isPickupMode = DeliveryModeService.isPickupMode();

      if (this.onlineInfo) {
        this.onlineInfo.style.display = isPickupMode ? 'none' : '';
      }

      if (this.pickupContent) {
        this.pickupContent.style.display = isPickupMode ? '' : 'none';
      }

      if (this.warningEl && !isPickupMode) {
        this.warningEl.classList.remove('is-visible');
      }

      this._updateCartItemBadges();
    }

    _openStoreModal() {
      document.dispatchEvent(new CustomEvent('standalone-store-modal:open'));
    }

    _updateSelectorDisplay() {
      if (!this.selectorEl) return;

      const selectedStore = StoreDataService.getSelectedStore();
      const nameEl = this.selectorEl.querySelector('[data-cart-selected-store-name]');
      const btnEl = this.selectorEl.querySelector('[data-cart-store-selector-btn]');

      if (selectedStore) {
        if (nameEl) nameEl.textContent = selectedStore.name;
        if (btnEl) btnEl.textContent = 'Vaihda';
        this.selectorEl.classList.remove('cart-store-selector--no-selection');
      } else {
        if (nameEl) nameEl.textContent = 'Ei valittu';
        if (btnEl) btnEl.textContent = 'Valitse myymälä';
        this.selectorEl.classList.add('cart-store-selector--no-selection');
      }
    }

    async _checkAvailability() {
      const cartItems = window.MurrenMurkina.cartItems || [];
      
      if (cartItems.length === 0) {
        this._hideWarning();
        return;
      }

      // Batch fetch all cart items availability
      const variantGids = cartItems.map(item => item.variantGid);
      this._cartAvailability = await StorefrontAPI.getMultipleVariantsAvailability(variantGids);

      this._updateCartItemBadges();

      if (!DeliveryModeService.isPickupMode()) {
        this._hideWarning();
        return;
      }

      if (!this.warningEl) return;

      const selectedStore = StoreDataService.getSelectedStore();
      
      if (!selectedStore) {
        this._hideWarning();
        return;
      }

      const unavailable = [];

      for (const item of cartItems) {
        const availData = this._cartAvailability[item.variantGid];
        if (availData) {
          const qty = availData.locations[selectedStore.locationId]?.quantity || 0;
          if (qty < item.quantity) {
            unavailable.push({
              title: item.productTitle,
              variant: item.variantTitle !== 'Default Title' ? item.variantTitle : null,
              quantity: item.quantity,
              available: qty
            });
          }
        }
      }

      if (unavailable.length > 0) {
        this._showWarning(selectedStore, unavailable);
      } else {
        this._hideWarning();
      }
    }

    _showWarning(store, unavailableProducts) {
      const storeNameEl = this.warningEl.querySelector('[data-warning-store-name]');
      const productsContainer = this.warningEl.querySelector('[data-warning-products]');
      const productsList = this.warningEl.querySelector('[data-warning-products-list]');

      if (storeNameEl) storeNameEl.textContent = store.name;

      if (productsContainer && productsList) {
        productsList.innerHTML = '';
        
        unavailableProducts.forEach(product => {
          const li = document.createElement('li');
          li.className = 'cart-store-warning__product-item';
          
          let text = product.title;
          if (product.variant) text += ` - ${product.variant}`;
          
          if (product.available > 0) {
            const availText = Utils.formatQuantityText(product.available);
            text += ` (tarvitset ${product.quantity} kpl, saatavilla ${availText})`;
          } else {
            text += ' (ei saatavilla)';
          }
          
          li.textContent = text;
          productsList.appendChild(li);
        });
        
        productsContainer.style.display = 'block';
      }

      this.warningEl.classList.add('is-visible');
    }

    _hideWarning() {
      this.warningEl?.classList.remove('is-visible');
    }

    /**
     * v5.5.0: Now uses onlineLocationId for online mode stock checks
     * v5.2.0: Now uses isCustomOrder from API data for cart item badges
     */
    _updateCartItemBadges() {
      const selectedStore = StoreDataService.getSelectedStore();
      const cartItems = window.MurrenMurkina.cartItems || [];
      const isPickupMode = DeliveryModeService.isPickupMode();
      
      const cartDrawer = document.querySelector('[data-name="cart-drawer"]');
      if (!cartDrawer) return;

      const cartItemElements = cartDrawer.querySelectorAll('.cart-item, [data-cart-item], cart-item');
      
      let hasCustomOrderItem = false;
      let hasUnavailableOnlineItem = false; // v5.5.0: Track if any item is unavailable for online delivery
      
      cartItemElements.forEach((itemEl, index) => {
        const existingBadge = itemEl.querySelector('.cart-item-availability');
        if (existingBadge) existingBadge.remove();

        let variantId = itemEl.dataset.variantId || itemEl.dataset.id;
        
        if (!variantId) {
          const link = itemEl.querySelector('a[href*="variant="]');
          if (link) {
            const match = link.href.match(/variant=(\d+)/);
            if (match) variantId = match[1];
          }
        }

        if (!variantId && cartItems[index]) {
          variantId = cartItems[index].variantId;
        }

        if (!variantId) return;

        // Skip if this variant is not in our cartItems (e.g., gift cards were filtered out)
        const cartItem = cartItems.find(item => item.variantId === String(variantId));
        if (!cartItem) return;

        const variantGid = Utils.variantIdToGid(variantId);
        const availData = this._cartAvailability[variantGid];
        
        if (!availData) return;

        const neededQty = cartItem.quantity || 1;

        const badge = document.createElement('div');
        badge.className = 'cart-item-availability';

        if (isPickupMode) {
          if (!selectedStore) {
            badge.classList.add('cart-item-availability--warning');
            badge.innerHTML = `
              <span class="cart-item-availability__dot"></span>
              <span>Valitse noutomyymälä</span>
            `;
          } else {
            const storeQty = availData.locations[selectedStore.locationId]?.quantity || 0;
            
            if (storeQty >= neededQty) {
              badge.classList.add('cart-item-availability--available');
              badge.innerHTML = `
                <span class="cart-item-availability__dot"></span>
                <span>Saatavilla: ${selectedStore.name}</span>
              `;
            } else if (storeQty > 0) {
              const qtyText = Utils.formatQuantityText(storeQty);
              badge.classList.add('cart-item-availability--partial');
              badge.innerHTML = `
                <span class="cart-item-availability__dot"></span>
                <span>Vain ${qtyText}: ${selectedStore.name}</span>
              `;
            } else {
              badge.classList.add('cart-item-availability--unavailable');
              badge.innerHTML = `
                <span class="cart-item-availability__dot"></span>
                <span>Ei saatavilla: ${selectedStore.name}</span>
              `;
            }
          }
        } else {
          // v5.5.1: BUGFIX - Use CONFIG.onlineLocationId which has fallback
          // Previously used window.MurrenMurkina.config which may not exist on collection pages
          const onlineLocationId = CONFIG.onlineLocationId;
          const onlineQty = onlineLocationId 
            ? (availData.locations[onlineLocationId]?.quantity || 0)
            : 0;
          const isCustomOrder = availData.isCustomOrder || false;
          
          if (onlineQty >= neededQty) {
            badge.classList.add('cart-item-availability--available');
            badge.innerHTML = `
              <span class="cart-item-availability__dot"></span>
              <span>Varastossa</span>
            `;
          } else if (onlineQty > 0) {
            const qtyText = Utils.formatQuantityText(onlineQty);
            badge.classList.add('cart-item-availability--partial');
            badge.innerHTML = `
              <span class="cart-item-availability__dot"></span>
              <span>Vain ${qtyText} varastossa</span>
            `;
            hasUnavailableOnlineItem = true; // v5.5.0: Not enough stock for full order
          } else {
            // onlineQty === 0: Check if it's a custom order product
            if (availData.availableForSale && isCustomOrder) {
              badge.classList.add('cart-item-availability--warning');
              badge.innerHTML = `
                <span class="cart-item-availability__dot"></span>
                <span>Tilaustuote</span>
              `;
              hasCustomOrderItem = true;
            } else if (availData.availableForSale) {
              // availableForSale but not custom order and no online stock - show as unavailable
              badge.classList.add('cart-item-availability--unavailable');
              badge.innerHTML = `
                <span class="cart-item-availability__dot"></span>
                <span>Ei varastossa</span>
              `;
              hasUnavailableOnlineItem = true; // v5.5.0: No online stock
            } else {
              badge.classList.add('cart-item-availability--unavailable');
              badge.innerHTML = `
                <span class="cart-item-availability__dot"></span>
                <span>Ei varastossa</span>
              `;
              hasUnavailableOnlineItem = true; // v5.5.0: Not available for sale
            }
          }
        }

        const titleContainer = itemEl.querySelector('.cart-item__title');
        if (titleContainer) {
          titleContainer.parentElement.insertBefore(badge, titleContainer.nextSibling);
        }
      });

      this._updateDeliveryTimeText(hasCustomOrderItem, hasUnavailableOnlineItem);
    }

    /**
     * v5.5.0: Now also handles unavailable online items - hides delivery time when items can't be shipped
     */
    _updateDeliveryTimeText(hasCustomOrderItem, hasUnavailableOnlineItem = false) {
      if (!this.onlineInfo) return;
      
      const timeText = this.onlineInfo.querySelector('span');
      if (!timeText) return;

      // v5.5.0: Hide delivery time info if any item is unavailable for online delivery
      if (hasUnavailableOnlineItem && !hasCustomOrderItem) {
        // Items unavailable but not custom order - hide delivery time completely
        this.onlineInfo.style.display = 'none';
      } else if (hasCustomOrderItem) {
        // Has custom order items - show extended delivery time
        this.onlineInfo.style.display = '';
        timeText.textContent = 'Toimitusaika 7-30 arkipäivää (sisältää tilaustuotteita)';
        this.onlineInfo.classList.add('cart-delivery-info--warning');
      } else {
        // All items available - show normal delivery time
        this.onlineInfo.style.display = '';
        timeText.textContent = 'Toimitus 1-3 arkipäivässä';
        this.onlineInfo.classList.remove('cart-delivery-info--warning');
      }
    }
  }

  // =============================================================================
  // INITIALIZATION
  // =============================================================================
  
  function init() {
    if (!customElements.get('store-availability')) {
      customElements.define('store-availability', StoreAvailability);
    }

    window.MurrenMurkina._standaloneModal = new StandaloneStoreModal();
    window.MurrenMurkina._cartStoreWarning = new CartStoreWarning();
    
    // Initialize cart attributes on page load based on saved delivery mode
    const savedMode = DeliveryModeService.getMode();
    CartAttributesService.updateForDeliveryMode(savedMode);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Export public API
  window.MurrenMurkina.StorefrontAPI = StorefrontAPI;
  window.MurrenMurkina.StoreDataService = StoreDataService;
  window.MurrenMurkina.DeliveryModeService = DeliveryModeService;
  window.MurrenMurkina.CartAttributesService = CartAttributesService;
  window.MurrenMurkina.Utils = Utils;
  window.MurrenMurkina.CONFIG = CONFIG; // v5.5.1: Export CONFIG for QuickBuyManager

})();